/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ARSFormView.tsx (ARS - Route Selections)
 ??? Created: 2025-01-15
 ?? Description:
    Form view for creating and editing ARS profiles.
    Manages route associations with priority-based ordering.

 ?? Notes:
    - Refactored from monolithic 670-line file
    - Logic delegated to hooks/
    - API calls moved to api/
    - Route management helpers extracted to utils/

 ?? Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Route, Search, Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Copy } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { Label } from '../../components/ui/label';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import SelectTimeGroup from '../../components/ui/SelectTimeGroup';
import SelectOutboundRoute from '../../components/ui/SelectOutboundRoute';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useArsData } from './hooks/useArsData';
import { useArsForm } from './hooks/useArsForm';
import { fetchOutboundRoutes, fetchTimeGroups, fetchArsProfiles, deleteArsProfileById } from './api/arsApi';
import type { OutboundRouteOption, TimeGroupOption, QuickListItem } from './types';

const QUICK_LIST_LIMIT = 100;
const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i; // Accepts numeric and Sqid-encoded IDs


export default function ARSFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotifications();

  const id = params.id ?? 'new';
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = id !== 'new';

  const { formData: initialData, isLoading } = useArsData(id, duplicateId);

  const {
    formState,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    addRoute,
    removeRoute,
    moveRoute,
    updateRoute,
    handleSave,
    handleCancel
  } = useArsForm(id, duplicateId, initialData);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();
    // If no changes were detected, return current formState to allow navigation
    if (!savedData) {
      return formState;
    }
    return savedData;
  }, [handleSave, formState]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-service/route-selections',
    newPath: '/pbx/class-of-service/route-selections/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-service/route-selections/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => formState,
    storageKey: 'ars:saveAndNewData',
  });

  const [outboundRoutes, setOutboundRoutes] = useState<OutboundRouteOption[]>([]);
  const [timeGroups, setTimeGroups] = useState<TimeGroupOption[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListProfiles, setQuickListProfiles] = useState<QuickListItem[]>([]);
  const quickListLoadingRef = useRef(false);

  // Load resources (outbound routes and time groups)
  const showErrorRef = useRef(showError);
  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const [routes, groups] = await Promise.all([
          fetchOutboundRoutes(QUICK_LIST_LIMIT),
          fetchTimeGroups(QUICK_LIST_LIMIT)
        ]);
        setOutboundRoutes(routes);
        setTimeGroups(groups);
      } catch (error) {
        const message = error instanceof Error ? error.message : t('ars.messages.loadError');
        showErrorRef.current(message);
      }
    };

    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !ID_PATTERN.test(id)) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteArsProfileById(id);

      showSuccess(t('ars.messages.deleteSuccess'));
      navigate('/pbx/class-of-service/route-selections');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('ars.messages.deleteError');
      showError(message);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !ID_PATTERN.test(id)) return;
    navigate(`/pbx/class-of-service/route-selections/new?duplicate=${id}`);
  }, [isEditMode, id, navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    try {
      const result = await fetchArsProfiles({
        page: 1,
        limit: QUICK_LIST_LIMIT,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      setQuickListProfiles(result.data);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('ars.messages.loadError');
      showError(message);
      setQuickListProfiles([]);
    } finally {
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListProfiles;
    }

    return quickListProfiles.filter((profile) =>
      profile.name?.toLowerCase().includes(query) ||
      profile.description?.toLowerCase().includes(query)
    );
  }, [quickListProfiles, quickListSearch]);

  const handleQuickListSelect = useCallback((profileId: string | number) => {
    setShowQuickList(false);
    setQuickListSearch('');
    navigate(`/pbx/class-of-service/route-selections/${profileId}`, { replace: true });
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!formState) {
    return null;
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Route className="h-4 w-4 text-primary" />}
        title={isEditMode ? `${t('ars.editProfile')} - ${formState.name || ''}` : t('ars.createProfile')}
        backButtonText={t('common.list')}
        onBack={() => navigate('/pbx/class-of-service/route-selections')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('ars.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              loadQuickList().catch(() => undefined);
            }
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('ars.actions.add'),
            onClick: () => navigate('/pbx/class-of-service/route-selections/new')
          }
        ]}
      />

      <div className="mt-8 space-y-6 pb-24">
        <FormBox title={t('ars.sections.general')} icon={<Route className="h-5 w-5" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <SimpleTooltip
              content={t('ars.fields.name.tooltip')}
              title={t('ars.fields.name.label')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="name" className="text-left">
                {t('ars.fields.name.label')}
              </Label>
            </SimpleTooltip>
            <div>
              <Input
                id="name"
                value={formState.name}
                onChange={(event) => handleChange('name', event.target.value)}
                placeholder={t('ars.fields.name.placeholder')}
                error={errors.name}
              />
            </div>

            <SimpleTooltip
              content={t('ars.fields.description.tooltip')}
              title={t('ars.fields.description.label')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="description" className="text-left">
                {t('ars.fields.description.label')}
              </Label>
            </SimpleTooltip>
            <div>
              <Input
                id="description"
                value={formState.description}
                onChange={(event) => handleChange('description', event.target.value)}
                placeholder={t('ars.fields.description.placeholder')}
              />
            </div>

            <SimpleTooltip
              content={t('ars.fields.enabled.tooltip')}
              title={t('ars.fields.enabled.label')}
              position="top"
              maxWidth="300px"
            >
              <Label htmlFor="enabled" className="text-left">
                {t('ars.fields.enabled.label')}
              </Label>
            </SimpleTooltip>
            <div>
              <Toggle
                id="enabled"
                value={formState.enabled}
                onChange={(value) => handleChange('enabled', value)}
              />
            </div>
          </div>
        </FormBox>

        <FormBox title={t('ars.sections.routes')} icon={<Route className="h-5 w-5" />}>
          {errors.routes && (
            <p className="text-sm text-red-500 mb-4">{errors.routes}</p>
          )}

          <div className="space-y-3">
            {formState.routes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t('ars.fields.routes.noRoutes')}
              </div>
            ) : (
              formState.routes.map((route, index) => (
                <div key={route.clientId} className="border border-border rounded-lg p-3 bg-muted/20">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-semibold text-muted-foreground">
                        {index + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <SimpleTooltip content={t('ars.tooltips.moveUp')}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={index === 0}
                            onClick={() => moveRoute(index, -1)}
                            className="h-7 w-7 p-0"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('ars.tooltips.moveDown')}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={index === formState.routes.length - 1}
                            onClick={() => moveRoute(index, 1)}
                            className="h-7 w-7 p-0"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </SimpleTooltip>
                      </div>
                    </div>

                    <div className="flex-1 min-w-[200px]">
                      <SelectOutboundRoute
                        value={route.outboundRouteId}
                        onChange={(value) => updateRoute(index, 'outboundRouteId', value)}
                        routes={outboundRoutes}
                        placeholder={t('ars.fields.routes.outboundRoutePlaceholder')}
                        error={Boolean(errors[`route_${index}_outbound`])}
                      />
                      {errors[`route_${index}_outbound`] && (
                        <p className="text-sm text-red-500 mt-1">
                          {errors[`route_${index}_outbound`]}
                        </p>
                      )}
                    </div>

                    <div className="flex-1 min-w-[200px]">
                      <SelectTimeGroup
                        value={route.timeGroupId}
                        onChange={(value) => updateRoute(index, 'timeGroupId', value)}
                        timeGroups={timeGroups}
                        placeholder={t('ars.fields.routes.timeGroupPlaceholder')}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Toggle
                        value={route.enabled}
                        onChange={(value) => updateRoute(index, 'enabled', value)}
                        aria-label={t('ars.fields.routes.enabled')}
                      />
                      <SimpleTooltip content={t('ars.actions.removeRoute')}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRoute(index)}
                          className="h-9 w-9 p-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </SimpleTooltip>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <Button type="button" onClick={addRoute} size="sm" className="gap-2">
              <Plus className="h-4 w-4" />
              {t('ars.actions.addRoute')}
            </Button>
          </div>
        </FormBox>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        useSaveDropdown={true}
        saveText={t('ars.actions.save')}
        cancelText={t('ars.actions.cancel')}
        deleteText={t('common.delete')}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('ars.messages.confirmDeleteTitle')}
        message={t('ars.messages.confirmDeleteMessage', { name: formState.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('ars.actions.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={quickListProfiles.length === 0 && showQuickList}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => handleQuickListSelect((profile as any).uuid || profile.id)}
        renderItem={(profile) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect((profile as any).uuid || profile.id)}
            className="w-full p-3 text-left border border-border rounded-lg hover:bg-muted/30 transition-colors"
          >
            <div className="font-medium text-foreground truncate">{profile.name}</div>
            {profile.description && (
              <div className="text-sm text-muted-foreground truncate">
                {profile.description}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {(profile.route_count ?? 0)} {t('ars.routeCount')}
            </div>
          </button>
        )}
        searchPlaceholder={t('ars.actions.quickListSearch')}
        emptyMessage={t('ars.actions.quickListNoResults')}
        headerIcon={<Route className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
